import { useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { AuthLayout } from '../layout/AuthLayout'
import { ErrorText, Field, TextInput } from '../ui/primitives'

export default function AuthPage() {
  const { user, login } = useAuth()
  const navigate = useNavigate()
  const from = (useLocation().state as { from?: string } | null)?.from ?? '/'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  if (user) return <Navigate to={from} replace />

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      await login(email, password)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout eyebrow="01 — Sign in" title="Welcome back." lead="Sign in to manage your rooms and invites.">
      <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
        <Field label="Email">
          <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
        </Field>
        <Field label="Password">
          <TextInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            maxLength={128}
            autoComplete="current-password"
          />
        </Field>
        <ErrorText>{error}</ErrorText>
        <button className="btn-primary mt-2 h-11" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p className="mt-8 border-l-2 border-line pl-3 text-[14px] text-ink-3">
        Vexa is invite-only. Ask an admin to send you an invitation. Joining a meeting as a guest never
        needs an account.
      </p>
    </AuthLayout>
  )
}
